/**
 * Parse Google Maps URLs to extract coordinates and place information
 */

import { api } from './api';

/**
 * Extract coordinates from various Google Maps URL formats
 * Supports:
 * - https://www.google.com/maps/place/Name/@lat,lng,zoom
 * - https://www.google.com/maps/@lat,lng,zoom
 * - https://maps.app.goo.gl/... (resolves via backend)
 * - https://goo.gl/maps/... (resolves via backend)
 * - https://www.google.com/maps?q=lat,lng
 * 
 * @param {string} url - Google Maps URL
 * @param {boolean} shouldResolve - Whether to try resolving shortened URLs via backend
 * @returns {Promise<Object|null>} - { lat, lng, name } or null if parsing fails
 */
export async function parseGoogleMapsUrl(url, shouldResolve = true) {
  try {
    let urlToParse = url;
    
    // Check if it's a shortened URL that needs to be resolved
    const isShortened = url.includes('goo.gl') || url.includes('maps.app.goo.gl');
    
    if (isShortened && shouldResolve) {
      // Try to resolve via backend
      try {
        const response = await api.resolveURL(url);
        if (response && response.resolved_url) {
          urlToParse = response.resolved_url;
        }
      } catch (error) {
        console.error('Failed to resolve shortened URL:', error);
        return { error: 'resolve_failed', message: 'Failed to resolve shortened URL', url };
      }
    } else if (isShortened && !url.includes('@')) {
      // Shortened URL but we're not resolving
      return { error: 'shortened', url };
    }
    
    // Handle various Google Maps URL formats
    
    // Format 0: Data parameters (!3d = latitude, !4d = longitude)
    // Example: https://www.google.com/maps/place/.../@48.858844,2.294351,17z/data=!3d48.858370!4d2.294481
    const dataMatch = urlToParse.match(/!3d(-?\d+\.?\d+)!4d(-?\d+\.?\d+)/);
    if (dataMatch) {
      const lat = parseFloat(dataMatch[1]);
      const lng = parseFloat(dataMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        // Try to extract name from place if available
        const nameMatch = urlToParse.match(/\/place\/([^/@?]+)/);
        const name = nameMatch ? decodeURIComponent(nameMatch[1].replace(/\+/g, ' ')) : null;
        return { lat, lng, name };
      }
    }
    
    // Format 1: /place/Name/@lat,lng,zoom
    // Example: https://www.google.com/maps/place/Eiffel+Tower/@48.858844,2.294351,17z
    const placeMatch = urlToParse.match(/\/place\/([^/@?]+)[@\/].*?(-?\d+\.?\d+),(-?\d+\.?\d+)/);
    if (placeMatch) {
      const name = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
      const lat = parseFloat(placeMatch[2]);
      const lng = parseFloat(placeMatch[3]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng, name };
      }
    }

    // Format 2: /@lat,lng,zoom (without place name)
    // Example: https://www.google.com/maps/@48.858844,2.294351,17z
    const coordsMatch = urlToParse.match(/\/@(-?\d+\.?\d+),(-?\d+\.?\d+)/);
    if (coordsMatch) {
      const lat = parseFloat(coordsMatch[1]);
      const lng = parseFloat(coordsMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng, name: null };
      }
    }

    // Format 3: ?q=place+name or ?q=lat,lng
    const qMatch = urlToParse.match(/[?&]q=([^&]+)/);
    if (qMatch) {
      const query = decodeURIComponent(qMatch[1].replace(/\+/g, ' '));
      // Check if it's coordinates
      const coordMatch = query.match(/^(-?\d+\.?\d+),(-?\d+\.?\d+)$/);
      if (coordMatch) {
        const lat = parseFloat(coordMatch[1]);
        const lng = parseFloat(coordMatch[2]);
        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng, name: null };
        }
      }
      // Otherwise it's a place name search - we can't get coordinates from this
    }

    // Format 4: /maps/search/?api=1&query=...
    const apiMatch = urlToParse.match(/query=([^&]+)/);
    if (apiMatch) {
      const query = decodeURIComponent(apiMatch[1].replace(/\+/g, ' '));
      const coordMatch = query.match(/^(-?\d+\.?\d+),(-?\d+\.?\d+)$/);
      if (coordMatch) {
        const lat = parseFloat(coordMatch[1]);
        const lng = parseFloat(coordMatch[2]);
        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng, name: null };
        }
      }
    }

    // Format 5: Try to find any lat,lng pattern in the URL
    const anyCoords = urlToParse.match(/(-?\d+\.?\d{4,}),(-?\d+\.?\d{4,})/);
    if (anyCoords) {
      const lat = parseFloat(anyCoords[1]);
      const lng = parseFloat(anyCoords[2]);
      // Validate that these look like real coordinates
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng, name: null };
      }
    }

    return null;
  } catch (error) {
    console.error('Error parsing Google Maps URL:', error);
    return null;
  }
}

/**
 * Validate if a string looks like a Google Maps URL
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
export function isGoogleMapsUrl(url) {
  return /^https?:\/\/(www\.)?google\.[a-z]+\/maps/.test(url) || 
         /^https?:\/\/maps\.app\.goo\.gl/.test(url) ||
         /^https?:\/\/goo\.gl\/maps/.test(url);
}
